import * as React from 'react';
import MuiButton, { type ButtonProps as MuiButtonProps, type ButtonTypeMap } from '@mui/material/Button';
import type { OverridableComponent, OverrideProps } from '@mui/material/OverridableComponent';

export type BootstrapColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'info'
  | 'warning'
  | 'light'
  | 'dark'
  | 'link';
export type ButtonColor = BootstrapColor | `outline-${Exclude<BootstrapColor, 'link'>}`;
export type ButtonSize = 'sm' | 'md' | 'lg';

/** The reactstrap-shaped props this adapter adds on top of MUI's own. */
interface ZuulButtonOwnProps {
  /** reactstrap-compatible color; maps to a MUI color + variant pair. */
  color?: ButtonColor;
  /** reactstrap `outline` → MUI variant='outlined'. */
  outline?: boolean;
  size?: ButtonSize;
  /** reactstrap `block` → MUI fullWidth. */
  block?: boolean;
  /** Escape hatch: an explicit MUI variant wins over the color mapping. */
  variant?: MuiButtonProps['variant'];
}

/**
 * Polymorphic the way MUI's own Button is, so `<Button component={Link} to='/x' />` type-checks.
 * Every converted view already writes that; .tsx files are simply the first place it is checked.
 */
export interface ZuulButtonTypeMap<AdditionalProps = {}, RootComponent extends React.ElementType = 'button'> {
  props: Omit<ButtonTypeMap<AdditionalProps, RootComponent>['props'], 'color' | 'size' | 'variant'> &
    ZuulButtonOwnProps;
  defaultComponent: RootComponent;
}

export type ButtonProps<RootComponent extends React.ElementType = 'button', AdditionalProps = {}> = OverrideProps<
  ZuulButtonTypeMap<AdditionalProps, RootComponent>,
  RootComponent
> & {
  component?: React.ElementType;
};

interface Mapped {
  color: MuiButtonProps['color'];
  variant: NonNullable<MuiButtonProps['variant']>;
  sx?: MuiButtonProps['sx'];
}

const COLOR_MAP: Record<BootstrapColor, Mapped> = {
  primary: { color: 'primary', variant: 'contained' },
  secondary: { color: 'secondary', variant: 'contained' },
  success: { color: 'success', variant: 'contained' },
  danger: { color: 'error', variant: 'contained' },
  info: { color: 'info', variant: 'contained' },
  warning: { color: 'warning', variant: 'contained' },
  light: { color: 'inherit', variant: 'outlined' },
  dark: {
    color: 'inherit',
    variant: 'contained',
    sx: { bgcolor: 'grey.800', color: 'common.white', '&:hover': { bgcolor: 'grey.900' } },
  },
  link: { color: 'primary', variant: 'text' },
};

const SIZE_MAP: Record<ButtonSize, NonNullable<MuiButtonProps['size']>> = {
  sm: 'small',
  md: 'medium',
  lg: 'large',
};

/** Resolves a reactstrap color (plus the `outline` flag) to MUI's color/variant pair. */
export function mapButtonColor(color: ButtonColor = 'secondary', outline = false): Mapped {
  const prefixed = color.startsWith('outline-');
  const base = (prefixed ? color.slice('outline-'.length) : color) as BootstrapColor;
  const mapped = COLOR_MAP[base] ?? COLOR_MAP.secondary;
  if ((outline || prefixed) && mapped.variant === 'contained') {
    return { color: mapped.color, variant: 'outlined' };
  }
  return mapped;
}

/**
 * A button whose only child is an SVG icon reads as an icon control, and MUI's 64px `minWidth`
 * turns those into lopsided rectangles. Detected here (MUI icons carry `muiName: 'SvgIcon'`)
 * so every icon-only Button squares itself without call sites opting in.
 */
function isIconOnly(children: React.ReactNode): boolean {
  if (React.Children.count(children) !== 1) return false;
  const child = React.Children.toArray(children)[0];
  if (!React.isValidElement(child)) return false;
  // @mui/icons-material components are memo(forwardRef(fn)) with muiName on the inner fn,
  // so the marker has to be dug out from under both wrappers.
  type Marked = { muiName?: string; type?: Marked; render?: Marked };
  const t = child.type as Marked;
  const muiName = t?.muiName ?? t?.render?.muiName ?? t?.type?.muiName ?? t?.type?.render?.muiName;
  return muiName === 'SvgIcon';
}

const ICON_ONLY_SX = { minWidth: 0, padding: '5px' } as const;

/**
 * MUI Button with reactstrap's prop surface, so converting a file is an import swap.
 * Font Awesome `<i>` children still render during the migration; prefer `startIcon` in new code.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { color, outline, size = 'md', block, variant, sx, ...rest },
  ref
) {
  const mapped = mapButtonColor(color, outline);
  const squared = isIconOnly(rest.children) ? ICON_ONLY_SX : null;
  const mergedSx = [mapped.sx, squared, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean);
  return (
    <MuiButton
      ref={ref}
      color={mapped.color}
      variant={variant ?? mapped.variant}
      size={SIZE_MAP[size]}
      fullWidth={block}
      sx={mergedSx}
      {...rest}
    />
  );
}) as OverridableComponent<ZuulButtonTypeMap>;
